export interface Project {
  id: string;
  title: string;
  description: string;
  fullDescription?: string;
  image: string;
  images?: string[];
  tags: string[];
  githubUrl?: string;
  liveUrl?: string;
  featured?: boolean;
  category: string;
  challenges?: string;
  outcome?: string;
  files?: Array<{
    name: string;
    url: string;
    path?: string;
    type: 'pdf' | 'step' | 'stl' | 'zip' | 'other';
    size?: string;
  }>;
  completionDate: string;
  /** Optional badge on cards / detail (e.g. NDA notice) */
  contextLabel?: string;
}

//   {
//     id: '1',
//     title: 'Project Title',
//     description: 'A brief one-line description of your project.',
//     fullDescription: `A detailed description of your project. This should include:

// Key features:
// - Feature 1
// - Feature 2
// - Feature 3

// Technical components:
// - Technology 1
// - Technology 2
// - Technology 3

// What makes this project unique or challenging?`,
//     image: '/images/projects/project1.jpg', // You'll need to add your own images
//     tags: ['Technology1', 'Technology2', 'Category'],
//     githubUrl: 'https://github.com/yourusername/project1',
//     liveUrl: 'https://project1-demo.com',
//     featured: true,
//     category: 'embedded', // or 'robotics', 'firmware', 'hardware'
// files: [
//   {
//     name: 'Documentation.pdf',
//     url: '/files/documentation.pdf',
//     type: 'pdf',
//     size: '2.4 MB'
//   }
// ]
//     challenges: 'Describe the main challenges you faced during development and how you overcame them.',
//     outcome: 'Describe the results and impact of your project. What did you learn? What was the outcome?'
//   },

// project data
export const projectsData: Project[] = [
  {
    id: 'nda-ble',
    title: 'NDA hardware and software integration',
    description:
      'Firmware and BLE-connected cross-platform mobile app (iOS/Android). Full BLE protocol stack and an adaptive scheduling approach using statistical pattern detection and confidence scoring. Details under NDA.',
    fullDescription: `<p>Firmware and BLE-connected cross-platform mobile app (iOS/Android). Implemented the full BLE protocol stack and an adaptive scheduling approach using statistical pattern detection and confidence scoring.</p><p>Product details, client, and domain are covered by an NDA.</p>`,
    image: '/images/projects/hardwareIOSapp.png',
    images: ['/images/projects/hardwareIOSapp.png'],
    tags: ['BLE', 'Firmware', 'React Native', 'Mobile', 'TypeScript', 'NDA'],
    featured: true,
    category: 'firmware',
    completionDate: '2026-04-01',
    contextLabel: 'NDA (active)',
  },
  {
    id: 'live-transcription',
    title: 'Live Transcription & Fact-Checker',
    description:
      'Browser app with React and TypeScript frontend and FastAPI backend. WebSocket streaming for low-latency audio; OpenAI Whisper and GPT-4o-mini for real-time transcription and live fact analysis.',
    fullDescription: `<p>Browser app with a React and TypeScript frontend and a FastAPI backend. Uses WebSocket streaming for low-latency audio and OpenAI Whisper plus GPT-4o-mini for real-time transcription and live fact analysis.</p>`,
    image: '/images/projects/factchecker.png',
    images: ['/images/projects/factchecker.png'],
    tags: ['React', 'TypeScript', 'FastAPI', 'WebSockets', 'OpenAI'],
    featured: false,
    category: 'web',
    completionDate: '2025-05-15',
  },
  {
    id: '1',
    title: 'Drone PID Simulation',
    description: 'Interactive simulation of a drone control system using PID controllers to demonstrate stabilization techniques and flight dynamics.',
    fullDescription: `This interactive simulation demonstrates how PID (Proportional-Integral-Derivative) controllers work in drone stabilization systems. The project allows users to adjust PID parameters in real-time and observe how they affect drone flight stability and performance.

Key features:
- Real-time visualization of drone height control using different PID algorithms
- Interactive parameter tuning for Kp, Ki, and Kd values with immediate feedback
- Multiple control algorithm implementations (Standard, Fuzzy Logic, Cascaded, and Nonlinear PID)
- Performance comparison tools to analyze different controller configurations
- Realistic physics simulation with adjustable drone parameters (mass, propeller size, thrust)

Technical components:
- React with TypeScript for robust front-end development
- Real-time data visualization using Recharts library
- Custom physics engine simulating drone dynamics with gravity and drag
- Advanced PID controller implementations with anti-windup protection
- State management with Context API for simulation data
- Data export capabilities for further analysis`,
    image: 'https://images.unsplash.com/photo-1487887235947-a955ef187fcc', // Drone in mid-air image
    images: [
      'https://images.unsplash.com/photo-1487887235947-a955ef187fcc',
      '/images/projects/pid_sim/PID_Sim4.JPG',
      '/images/projects/pid_sim/PID_Sim2.JPG',
      '/images/projects/pid_sim/PID_Sim5.JPG',
    ],
    tags: ['React', 'TypeScript', 'Control Systems', 'PID Controllers', 'Data Visualization', 'Physics Simulation'],
    githubUrl: 'https://github.com/Jenaro98/code-capability-explorer',
    liveUrl: 'https://drone-pid-simulation.netlify.app/',
    featured: true,
    category: 'robotics', // This fits well in the robotics category since it's about drone control
    challenges: 'Implementing realistic physics behavior for the drone presented significant challenges, particularly in creating accurate drag models and ensuring numerical stability in the simulation. Another major challenge was developing multiple PID controller variants (Fuzzy Logic, Cascaded, Nonlinear) that demonstrate different control approaches while maintaining a consistent interface. Ensuring the visualization remained responsive and accurate during parameter changes required careful optimization of the rendering pipeline.',
    outcome: 'This project successfully demonstrates complex control theory concepts through an accessible, interactive interface. Users can gain intuition about how PID controllers work and how parameter tuning affects system response. The simulation has proven to be a valuable educational tool for students learning about control systems, and the comparison feature allows for empirical analysis of different control strategies. Through development, I gained deeper expertise in both control systems theory and interactive visualization techniques.',
    completionDate: '2025-03-04',
  },
  {
  id: '11',
    title: 'Delegate-it.io',
    description:
      'TypeScript and Supabase web app with real-time collaboration and QR-code-based instant event joining. Full UI and branding designed in Figma.',
  fullDescription: `Delegate-it is a web app designed to simplify task management and coordination through real-time collaboration. It allows users to create a session and share tasks instantly via QR codes. Participants can join sessions without accounts, claim tasks, ask questions, and mark tasks complete, all synchronized live.

Key features:
- Real-time task updates across users using Supabase
- QR code generation for quick session joining
- Role-based functionality for creators and participants
- Task claiming, completion tracking, and discussion threads
- Clean, mobile-responsive interface with instant updates

Technical components:
- Built with React and Supabase for seamless backend and real-time sync
- Uses Netlify for deployment and hosting
- Integrated Supabase Auth and Database for user management
- Optimized for minimal data egress and responsive performance
- Designed and developed fully by Jenaro Vega

Delegate-it was built as a fast, intuitive tool for both work and personal use, whether delegating household chores, planning events, or managing group projects.`,
  image: '/images/projects/delegate-it/DI-1.JPG',
  images: [
    '/images/projects/delegate-it/DI-1.JPG',
    '/images/projects/delegate-it/DI-2.JPG'
  ],
  tags: ['React', 'Supabase', 'QR Codes', 'Real-time Sync', 'Netlify', 'Team Collaboration'],
  githubUrl: '',
  liveUrl: 'https://delegate-it.netlify.app/',
  featured: true,
  category: 'web',
  challenges: 'Achieving smooth real-time synchronization across participants required optimizing Supabase subscriptions to prevent redundant data loads. Additionally, balancing simplicity and flexibility in the UI was a challenge to ensure it worked equally well for events, work teams, and families.',
  outcome: 'Delegate-it successfully demonstrates live, collaborative task delegation in a lightweight and intuitive way. The app is currently deployed and stable, serving as a solid foundation for expanding into mobile and premium tiers. Development reinforced expertise in Supabase’s real-time capabilities and scalable UI design.',
  completionDate: '2025-08-01',
},
{
  id: '13',
    title: 'AI Budget Planner',
    description: 'A privacy-friendly budgeting web app that runs entirely in the browser and saves data to local storage. No logins, no database costs.',
  fullDescription: `AI Budget Planner helps users build a simple, sustainable monthly budget without giving up privacy or paying for backend services. Everything runs client-side: your income, categories, and expenses are stored in your device’s browser storage so the app remains fast, free, and accessible to anyone.

Key features:
- Local-first design: all data is saved to your browser’s local storage (no accounts, no server database)
- Quick entry for income and expenses with custom categories
- Running totals and monthly budget targets
- Visual breakdowns to spot overspending at a glance
- Lightweight, responsive UI that works great on desktop and mobile

Technical components:
- Front-end only architecture deployed on Netlify
- State persisted via window.localStorage for zero hosting cost
- Client-side calculations and charts for instant feedback
- No external database or authentication required

This project focuses on speed, simplicity, and user privacy, making budgeting approachable for anyone while keeping operational costs at zero.`,
  image: '/images/projects/ai-budget-planner/BudgetAI-1.JPG',
  images: [
    '/images/projects/ai-budget-planner/BudgetAI-1.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-2.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-3.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-4.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-5.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-6.JPG',
    '/images/projects/ai-budget-planner/BudgetAI-7.JPG'
  ],
  tags: ['Local-First', 'React', 'Netlify', 'Budgeting', 'Data Visualization'],
  githubUrl: '',
  liveUrl: 'https://aibudgetplanner.netlify.app/',
  featured: true,
  category: 'web',
  challenges: 'Designing a fully client-side budgeting flow while keeping the UI clear and calculations accurate. Ensuring localStorage reads/writes remain robust across sessions and avoiding data loss on refresh or navigation.',
  outcome: 'Delivers a fast, private budgeting experience with zero recurring costs. The local-first approach keeps the app simple to maintain and free for users while remaining responsive and reliable.',
  completionDate: '2025-09-15'
},
  {
    id: "2",
    title: "FOCUS - Pomodoro Timer App",
    description: "A sleek productivity app with interactive UI that helps users focus with the Pomodoro technique.",
    fullDescription: `A modern Pomodoro timer application that helps users improve productivity through focused work sessions and scheduled breaks. The app features a minimalist design with an engaging interactive background.

Key features:
- Custom timer settings for focus and break sessions
- Interactive background with particle animation and click effects
- Task management system with to-do list functionality
- Browser tab notifications for timer completion
- Visual notifications when timers complete

Technical components:
- React with TypeScript for type-safe component architecture
- State management via Context API with reducer pattern
- Shadcn/UI components with custom styling
- Tailwind CSS for responsive design and animations
- Local storage persistence for user settings and tasks

What makes this project unique or challenging is the combination of productivity functionality with engaging visual elements. The interactive background creates a meditative atmosphere while maintaining focus on the core timer functionality. The app also implements browser tab title updates to track time even when not actively viewing the app.`,
    image: "https://images.unsplash.com/photo-1504507926084-34cf0b939964?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    images: [
      'https://images.unsplash.com/photo-1504507926084-34cf0b939964?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      '/images/projects/pomo_focus/pomo_1.JPG',
    ],
    tags: ["React", "TypeScript", "Tailwind CSS", "Productivity"],
    githubUrl: "https://github.com/Jenaro98/aura-focus-flow",
    liveUrl: "https://pomodoro-focus-flow.netlify.app",
    featured: false,
    category: "web",
    challenges: "One of the main challenges was creating an interactive background that enhances the user experience without being distracting. This required careful tuning of the particle system animation parameters and explosion effects. Another challenge was maintaining timer functionality even when the app is in a background tab, which was solved using document title updates that show the current timer. Ensuring smooth transitions between timer states and implementing proper state management with the reducer pattern required thoughtful architecture planning.",
    outcome: "The result is a productivity tool that is both functional and visually engaging. The project demonstrates how to balance aesthetics with functionality in a web application while maintaining performance. Through this project, I deepened my knowledge of React's Context API, custom hooks, and animation techniques in modern web development.",
    completionDate: '2025-04-01',
  },
  {
    id: "3",
    title: "Gymnograph - Workout Tracker",
    description: "A comprehensive workout tracking app to plan, record, and analyze your fitness progress.",
    fullDescription: `Gymnograph is a fully responsive workout tracking application that helps fitness enthusiasts plan, record, and analyze their fitness journey. This web application stores all workout data in the browser's local storage, making it a portable solution that doesn't require server-side infrastructure.

Key features:
- User authentication system with secure local storage
- Comprehensive workout planning and tracking
- Exercise library with customizable templates
- Detailed exercise progress analytics and visualizations  
- Workout calendar for scheduling and planning
- Mobile-responsive design for on-the-go tracking

Technical components:
- React 18 with TypeScript for type-safe development
- State management using React Context API
- Local storage for persistent data without a backend
- Recharts for interactive data visualization
- Tailwind CSS and shadcn/ui for responsive UI components
- Lucide React for consistent iconography
- Mobile-first responsive design with custom hooks

What makes this project unique or challenging?
Gymnograph delivers a full-featured workout tracking experience without requiring a backend database. The application implements a sophisticated local storage data management system that maintains user data integrity across sessions while providing features typically found in server-based applications. The analytics system processes workout data to provide meaningful insights on progress, trends, and consistency.`,
    image: "https://images.unsplash.com/photo-1544033527-b192daee1f5b?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    images: [
      'https://images.unsplash.com/photo-1544033527-b192daee1f5b?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      '/images/projects/gymnograph/gymnograph1.JPG',
      '/images/projects/gymnograph/gymnograph3.JPG',
      '/images/projects/gymnograph/gymnograph4.JPG',
      '/images/projects/gymnograph/gymnograph2.JPG',
      
    ],
    tags: ["React", "TypeScript", "Tailwind CSS", "Local Storage", "Fitness"],
    githubUrl: "https://github.com/Jenaro98/iron-forge-tracker-log",
    liveUrl: "https://gymnograph.netlify.app",
    featured: false,
    category: "web",
    files: [],
    challenges: "The primary challenge in developing Gymnograph was creating a robust data management system using only client-side storage while maintaining data integrity. This required careful design of the data structures and relationships between workouts, exercises, and sets. Additionally, implementing the analytics features demanded complex data processing on the client side to calculate meaningful metrics like workout streaks, progress over time, and volume progression. The mobile responsiveness required thoughtful UI design to ensure the app remained fully functional and intuitive on smaller screens.",
    outcome: "Gymnograph successfully demonstrates that powerful, feature-rich applications can be built without requiring server infrastructure. The application provides users with a complete workout tracking solution that works entirely in their browser, with data stored locally on their device. This approach ensures privacy and eliminates dependencies on external services while still delivering robust functionality. The implementation of responsive design ensures users can track their workouts on any device, making it a practical tool for gym-goers.",
    completionDate: '2025-04-29',
  },
  {
    id: "4",
    title: "Rhythm Realm",
    description: "A sleek habit tracking application with analytics, built with React and localStorage.",
    fullDescription: "Rhythm Realm is a dark-themed habit tracking application built with React, TypeScript, and Tailwind CSS. This application allows users to create, track, and visualize their daily habits through an intuitive and visually appealing interface. What makes this application unique is that all data is stored locally in the browser's localStorage, ensuring data persistence across sessions without requiring a database connection. Each user's data is stored only on the device they're using, making this perfect for personal use while maintaining privacy.\n\nKey features:\n- User authentication system with localStorage persistence\n- Habit creation, tracking, and deletion\n- Daily habit completion tracking with streaks\n- Comprehensive analytics dashboard with charts and statistics\n- Calendar view for visualizing habit completion over time\n- Profile management\n- Dark sleek UI with glassmorphism design\n\nTechnical components:\n- React with TypeScript for robust frontend development\n- Tailwind CSS with shadcn/ui components for sleek design\n- LocalStorage for data persistence across sessions\n- React Router for seamless navigation\n- React Hook Form for form management\n- Recharts for data visualization\n- Lucide React for consistent iconography\n\nWhat makes this project unique or challenging?\nThe primary challenge was implementing a fully functional habit tracking system without relying on a backend database. By leveraging localStorage, the application maintains all user data on the client side while still providing features typically associated with server-based applications, such as user authentication, data persistence, and complex analytics. The sleek dark UI with glassmorphism effects creates a modern, premium feel despite the application's lightweight architecture.",
    image: "https://images.unsplash.com/photo-1648051793958-983a634406d1?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    images: [
      'https://images.unsplash.com/photo-1648051793958-983a634406d1?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      '/images/projects/habits/habits1.JPG',
      '/images/projects/habits/habits2.JPG',
      
    ],
    tags: ["React", "TypeScript", "Tailwind CSS", "LocalStorage", "Habit Tracking"],
    githubUrl: "https://github.com/Jenaro98/rhythm-realm-tracker",
    liveUrl: "https://rhythm-realm-tracker.netlify.app",
    featured: false,
    category: "web",
    files: [],
    challenges: "The main challenge was creating a full-featured habit tracking system without a backend database. This required designing an efficient data structure in localStorage that could handle user authentication, habit tracking, and analytics calculations while maintaining good performance. Another significant challenge was ensuring data integrity across browser sessions and implementing proper date handling to accurately track habit completion streaks.",
    outcome: "Rhythm Realm successfully demonstrates that sophisticated web applications can be built entirely on the client side using localStorage. The project delivers a premium user experience with robust habit tracking features, insightful analytics, and an elegant UI, all without requiring a database. This makes it accessible for users who want a personal habit tracker that works across sessions on their device while maintaining complete data privacy. The application is lightweight, fast, and can be deployed easily to any web hosting service without backend configuration.",
    completionDate: '2025-04-25',
  },
  {
    id: "5",
    title: "LearnTree",
    description: "A hierarchical learning and note-taking application for organizing knowledge.",
    fullDescription: `A comprehensive knowledge management application designed to help users structure their learning journey through hierarchical topic trees.

Key features:
- Hierarchical knowledge organization with nested topics and subtopics
- Rich text note-taking with markdown support
- Progress tracking for each learning topic
- Bookmarking system for quick access to important content
- File and image attachments for enhanced note-taking
- Topic tagging for better organization and searchability

Technical components:
- React with TypeScript for frontend development
- Supabase for authentication and database management
- Shadcn UI components for consistent design
- Rich text editing with markdown support
- File upload and storage capabilities
- Progress tracking and visualization

What makes this project unique or challenging?
LearnTree solves the problem of fragmented knowledge by providing a structured approach to learning new subjects. Unlike traditional note-taking apps, it incorporates a hierarchical organization system that mimics how knowledge naturally branches out, making it easier for users to see connections between topics and track their progress across complex subjects.`,
    image: "https://images.unsplash.com/photo-1664526936959-84bdea9f37fa?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    images: [
      'https://images.unsplash.com/photo-1664526936959-84bdea9f37fa?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      '/images/projects/learntree/learntree3.JPG',
      '/images/projects/learntree/learntree1.JPG',
      '/images/projects/learntree/learntree2.JPG',
      
    ],
    tags: ["React", "TypeScript", "Supabase", "Shadcn UI", "Knowledge Management"],
    githubUrl: "https://github.com/Jenaro98/learn-tree-hub",
    liveUrl: "https://learntree.netlify.app",
    featured: false,
    category: "web",
    files: [],
    challenges: "A major challenge was designing an intuitive interface for managing hierarchical data while maintaining good performance with deeply nested structures. The team had to carefully design the database schema to efficiently store and query tree-like data. Another challenge was implementing a seamless topic reordering system with drag-and-drop functionality that persists user preferences across sessions. The password reset flow also required careful attention to ensure security while maintaining a good user experience.",
    outcome: "LearnTree successfully provides users with a powerful tool for structuring and managing their learning journey.",
    completionDate: '2025-04-23',
  },
  {
    id: "6",
    title: "MathesisWay",
    description:
      "Full-stack web app generating dynamic learning paths via OpenAI, YouTube, and Wikipedia APIs. Stripe payments, Supabase PostgreSQL schema, and serverless edge functions.",
    fullDescription: "MathesisWay is an interactive educational application that empowers users to create personalized learning paths for any subject. The platform leverages AI to generate detailed, structured learning content and helps users visualize knowledge connections. Users can select topics they want to learn about, customize their learning paths, and access rich, multi-format educational content.\n\nKey features:\n- User authentication and personalized dashboard\n- Topic creation and management\n- AI-generated hierarchical learning paths\n- Comprehensive educational content generation\n- Interactive content display with multiple views (explanation, visualization, examples, quiz, insights)\n- Content caching for offline access\n- Progress tracking across learning paths\n- Custom topic addition and management\n- Interactive quizzes with immediate feedback\n- Visual network representation of knowledge\n\nTechnical components:\n- React with TypeScript for frontend development\n- OpenAI API integration for content generation\n- Context API for state management\n- Local Storage for persistent data handling\n- React Router for navigation\n- Custom hooks for shared functionality\n- Shadcn UI components with Tailwind CSS for styling\n- Framer Motion for animations\n- Responsive design for all device sizes\n\nWhat makes this project unique or challenging?\nMathesisWay uniquely addresses the challenge of personalized learning by creating custom, AI-generated learning paths rather than offering pre-made courses. The application intelligently structures knowledge into connected nodes, which mirrors how our brains naturally learn. The technical challenge was implementing token-aware content generation that produces rich, multi-format educational materials while managing API limitations through smart batching and caching strategies.",
    image: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    images: [
      'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      '/images/projects/mathesis/mathesis1.JPG',
      '/images/projects/mathesis/mathesis2.JPG',
      '/images/projects/mathesis/mathesis3.JPG',
      '/images/projects/mathesis/mathesis4.JPG',
      '/images/projects/mathesis/mathesis5.JPG',
      '/images/projects/mathesis/mathesis6.JPG',
      '/images/projects/mathesis/mathesis7.JPG',
      
    ],
    tags: ["React", "TypeScript", "OpenAI", "Education", "AI", "Learning", "Knowledge Graph"],
    githubUrl: "",
    liveUrl: "https://mathesisway.netlify.app/",
    featured: true,
    category: "web",
    files: [],
    challenges: "The primary challenges included efficiently interfacing with the OpenAI API while respecting token limits and ensuring consistent, structured content generation. For larger topics, implementing an intelligent batching system was necessary to handle content generation across multiple API calls while maintaining a cohesive learning experience. Additionally, creating an intuitive UI for visualizing topic hierarchies and enabling user customization of learning paths required careful design considerations. Parsing and consistently formatting AI-generated content, especially ensuring valid JSON responses and proper markdown rendering, demanded robust error handling and data sanitization strategies.",
    outcome: "MathesisWay successfully delivers a personalized learning experience that adapts to users' interests while maintaining educational rigor. The application effectively creates structured knowledge paths that break down complex subjects into manageable, interconnected topics. Users can generate comprehensive educational content including explanations, visualizations, examples, quizzes, and insights, all tailored to their specific learning needs. The project demonstrates how AI can enhance rather than replace traditional learning by providing scaffolding for knowledge acquisition while maintaining human agency in the learning process. From a technical perspective, the application showcases effective state management, API integration, and user experience design patterns that could be applied to other educational technology projects.",
    completionDate: '2025-04-03',
  },
  {
    id: '7',
    title: 'Recyclables Pattern Recognition System',
    description: 'A smart classification system using sensors and k-nearest neighbors to detect and sort recyclable objects like cans and bottles.',
    fullDescription: `This embedded system classifies recyclable objects (plastic bottles, glass bottles, and aluminum cans) using real-time sensor input and a k-nearest neighbors (KNN) algorithm. The project is structured in three main phases: feature extraction, classification, and actuation.
  
  Key features:
  - Feature Extraction Phase: Uses a load cell to measure object weight and an RGB sensor to extract color data (red, green, blue values)
  - Classification Phase: Compares the input features to a 16-item training dataset using the Euclidean distance formula
  - KNN algorithm (k=3) identifies the category of the object based on the closest neighbors
  - Actuation Phase: Prints out the final predicted recyclable category
  
  Technical components:
  - Arduino Mega microcontroller
  - Load cell and amplifier for weight sensing
  - RGB sensor for color data capture
  - K-nearest neighbors classification algorithm implemented in C/C++
  - Serial output for displaying classification results
  - 100% classification accuracy on the test set of aluminum cans, plastic, and glass bottles
  
  `,
    image: 'https://img.youtube.com/vi/Gjqo00mEAyI/0.jpg',
    images: [
      'https://img.youtube.com/vi/Gjqo00mEAyI/0.jpg'
    ],
    tags: ['C++', 'Arduino', 'Embedded Systems', 'Sensors', 'KNN', 'Recycling'],
    githubUrl: null,
    liveUrl: 'https://youtu.be/Gjqo00mEAyI?si=RZp1XMlCQe0XOwLK',
    featured: true,
    category: 'embedded',
    challenges: 'Integrating real-time sensor data with a classification algorithm on an embedded system required managing limited memory and computation. Ensuring consistent and accurate feature extraction under variable lighting and object positions added complexity.',
    outcome: 'The project achieved 100% classification accuracy on test samples and demonstrated the effectiveness of combining lightweight ML algorithms like KNN with embedded hardware for real-world object classification. It deepened my understanding of sensor integration and data-driven logic in constrained environments.',
    completionDate: '2020-12-15',
  }
  ,
  {
    id: '8',
    title: 'PID Ball Lift Simulator',
    description: 'An embedded simulation of PID control for stabilizing a ball’s height using a fan system, with manual and automatic control modes.',
    fullDescription: `This PID Ball Lift Simulator demonstrates the core concepts of control systems using an embedded C program running on the Riverside-Irvine Microcontroller Simulator. The system models a ball lifted by fan power, governed by mass, gravity, and PID control to maintain a target height.
  
  Key features:
  - Auto PID mode dynamically adjusts fan power to stabilize the ball at a desired height
  - Manual mode allows users to control fan power themselves to experience tuning difficulty
  - Physics-based simulation with gravity and mass affecting ball behavior
  - Adjustable ball mass for demonstrating system response to load changes
  
  Technical components:
  - Written in C and executed on the Riverside-Irvine Microcontroller Simulator
  - Simulated real-time control loop implementing proportional-integral-derivative logic
  - Scaled math computations to overcome limited data range in microcontroller
  - Simulated ADC input and PWM-like fan output modeling
  - System feedback based on height error, allowing users to observe real PID behavior
  `,
    image: 'https://img.youtube.com/vi/Uy3c4JSQLos/0.jpg',
    images: [
      'https://img.youtube.com/vi/Uy3c4JSQLos/0.jpg'
    ],
    tags: ['C', 'Embedded Systems', 'PID Controller', 'Control Systems', 'Physics Simulation'],
    githubUrl: null,
    liveUrl: 'https://youtu.be/Uy3c4JSQLos?si=vaskBN8cdIIARhUd',
    featured: true,
    category: 'control-systems',
    challenges: 'One of the main challenges was handling floating-point-like precision within integer constraints by scaling values. Simulating real-time fan response, mass changes, and PID logic on limited microcontroller resources pushed careful structuring of the control loop and data management.',
    outcome: 'This project reinforced my understanding of control system fundamentals and challenged me to creatively simulate physical forces and feedback within constrained embedded environments. It also helped illustrate the value of PID tuning through both manual and automated comparisons.',
    completionDate: '2020-11-23',
  }
  ,
  {
    id: '9',
    title: 'Smart Chicken Coop Simulation',
    description: 'An embedded system simulation designed to automate temperature control, feeding, lighting, and egg counting for a chicken coop.',
    fullDescription: `This Smart Chicken Coop project simulates a fully automated embedded system designed to support healthy poultry conditions. Programmed in C and simulated on the Riverside-Irvine microcontroller platform, the system integrates multiple smart features to enhance coop management.
  
  Key features:
  - Temperature regulation between 65°F and 75°F for optimal chicken comfort
  - Automated food and water dispensing system
  - Motion-triggered outdoor night light to deter predators
  - Laser-triggered door mechanism for secure access
  - Egg counter system with digital display to track egg production
  
  Technical components:
  - Simulated on the Riverside-Irvine Microcontroller Simulator
  - Programmed in C for low-level control logic
  - Real-time event handling for temperature, motion, and laser inputs
  - Output control for actuators, lighting, and counters
  - Modular code structure to handle independent subsystems
  
  `,
    image: 'https://img.youtube.com/vi/orfReUYc8W0/0.jpg',
    images: [
      'https://img.youtube.com/vi/orfReUYc8W0/0.jpg'
    ],
    tags: ['C', 'Embedded Systems', 'Simulation', 'Automation', 'IoT', 'Microcontroller'],
    githubUrl: null,
    liveUrl: 'https://youtu.be/orfReUYc8W0?si=V7heWYPZ0XRNysHI',
    featured: true,
    category: 'embedded',
    challenges: 'Simulating multiple hardware subsystems and coordinating their interactions in a single microcontroller program posed architectural and timing challenges. Ensuring synchronized logic between sensors, actuators, and counters required thorough planning and debugging.',
    outcome: 'This project provided a comprehensive experience in embedded system design and simulation. It demonstrated the practical application of automation in agriculture and strengthened my skills in modular programming and embedded simulation environments.',
    completionDate: '2020-10-24',
  }
  ,
  {
    id: '10',
    title: 'Reaction Game',
    description: 'A reflex-based embedded game where users match arrow directions on an LED matrix using a joystick.',
    fullDescription: `Reaction Game is a fast-paced embedded systems project designed to test user reflexes. An arrow appears on an 8x8 red LED dot matrix, and the player must quickly match its direction using an analog joystick. As the game progresses, the arrows change more rapidly, increasing the difficulty. The player advances through 4 levels, each speeding up the response time required for a correct match.
  
  Key features:
  - Real-time arrow matching game using LED matrix and joystick input
  - Speed increases as the game progresses through 4 levels
  - Visual feedback displayed on Nokia5110 screen
  - Designed for user engagement and improving reflexes
  
  Technical components:
  - Programmed in C for the Atmega1284 microcontroller
  - Nokia5110 LCD for user interface and score tracking
  - 8x8 LED dot matrix with 16-pin control
  - Analog joystick (5 pins) for directional input
  - External buttons used for game control and resets
  - Timer interrupts and analog-to-digital conversion for responsiveness
  
  `,
    image: 'https://img.youtube.com/vi/kN6aUctTcOM/0.jpg',
    images: [
      'https://img.youtube.com/vi/kN6aUctTcOM/0.jpg'
    ],
    tags: ['C', 'Embedded Systems', 'ATmega1284', 'LED Matrix', 'Joystick Input', 'Real-Time Game'],
    githubUrl: null,
    liveUrl: 'https://youtu.be/kN6aUctTcOM?si=QhNrXjF9k_n8DtQO',
    featured: true,
    category: 'embedded',
    challenges: 'Handling real-time input and ensuring smooth gameplay logic using limited microcontroller resources was a major challenge. Interfacing the LED matrix, joystick, and screen simultaneously required careful timing and resource management.',
    outcome: 'The project successfully demonstrated a complete embedded game system. It improved my low-level hardware interfacing skills and deepened my understanding of real-time programming and user interaction design in C.',
    completionDate: '2019-12-08',
  }
  ,
  {
    id: "14",
    title: "Direqtr",
    description:
      "React Native / Expo app with multi-segment camera recording, countdowns, zoom overlays, and gallery export. Supabase auth with RLS, RevenueCat subscriptions, PostHog analytics.",
    fullDescription: `Direqtr is a mobile recording workflow built with React Native and Expo. It focuses on multi-segment capture, on-screen countdowns, zoom overlays, and exporting clips to a gallery experience.

Key features:
- Multi-segment camera recording and gallery export
- Countdown and zoom overlay UX for repeatable takes
- Supabase authentication with row-level security
- RevenueCat subscriptions and PostHog analytics

Technical components:
- React Native / Expo for iOS and Android
- Supabase (auth, database, policies)
- RevenueCat and PostHog integrations`,
    image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?q=80&w=2071&auto=format&fit=crop",
    images: [
      "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?q=80&w=2071&auto=format&fit=crop"
    ],
    tags: ["AI", "Video Production", "Creative Tools", "Web App"],
    githubUrl: "",
    liveUrl: "https://direqtr.app/",
    featured: true,
    category: "web",
    challenges: "Building an AI-powered video direction tool required balancing automation with creative control. Ensuring the AI suggestions were helpful without being prescriptive, and creating an interface that works for both beginners and experienced videographers presented unique UX challenges.",
    outcome: "Direqtr successfully demonstrates how AI can enhance creative workflows without replacing human creativity. The platform provides practical assistance throughout the video production process while keeping creators in control of their vision.",
    completionDate: "2025-11-01"
  },
  {
    id: "15",
    title: "LinkSign",
    description: "A free, hassle-free document signing web app that lets users collect signatures on PDFs through secure shareable links, no account required.",
    fullDescription: `LinkSign streamlines the document signing process by allowing users to upload a PDF, share a secure link, and collect signatures easily. The platform eliminates friction from signature collection by removing account creation requirements for signers.

Key features:
- PDF document signing capability with secure upload
- Shareable links for easy document distribution
- Password protection option for added security
- No account registration needed for signers
- Automatic document cleanup after 30 days for privacy
- Mobile-friendly interface for signing on any device

Technical components:
- Front-end focused architecture for fast, responsive experience
- Secure file handling with encryption
- Link generation system with optional password protection
- Automatic expiration and cleanup system
- Deployed on Netlify for reliable, fast hosting

LinkSign combines simplicity with security, enabling anyone to collect signatures on PDFs through shared links without the complexity of traditional e-signature platforms.`,
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop",
    images: [
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop"
    ],
    tags: ["Document Signing", "PDF", "Web App", "Netlify", "Security"],
    githubUrl: "",
    liveUrl: "https://linksign.netlify.app/",
    featured: true,
    category: "web",
    challenges: "Creating a secure document signing flow without requiring user accounts while maintaining document integrity and privacy was the primary challenge. Implementing automatic cleanup and ensuring the signing process works smoothly across all devices required careful consideration of edge cases.",
    outcome: "LinkSign delivers a streamlined signing experience that removes barriers to getting documents signed. The automatic cleanup feature ensures privacy while the no-account-required approach makes it accessible to anyone who needs to sign a document quickly.",
    completionDate: "2025-10-15"
  }
].filter(project => project.title !== '').sort((a, b) => {
  // Sort by completion date, newest first
  return new Date(b.completionDate).getTime() - new Date(a.completionDate).getTime();
});

export const getProjectById = (id: string): Project | undefined => {
  return projectsData.find(project => project.id === id);
};

export const getRelatedProjects = (currentId: string, category: string, limit = 3): Project[] => {
  return projectsData
    .filter(project => project.id !== currentId && project.category === category)
    .slice(0, limit);
};
